"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Mic, Video, Phone, Sparkles, MessageSquare, TrendingUp, Target, Lightbulb, CheckCircle2, User } from "lucide-react";
interface DemoMessage {
  role: "agent" | "user";
  text: string;
  delay: number;
}
interface DemoScores {
  clarity: number;
  structure: number;
  examples: number;
  confidence: number;
  posture: number;
}
interface DemoTip {
  type: "success" | "info";
  message: string;
  delay: number;
}
const DEMO_MESSAGES: DemoMessage[] = [{
  role: "agent",
  text: "Bonjour ! Je suis Claire, recruteuse chez TechCorp. Parlez-moi de vous.",
  delay: 0
}, {
  role: "user",
  text: "Bonjour ! Je suis développeur full-stack avec 3 ans d'expérience. J'ai travaillé principalement sur des projets e-commerce et SaaS.",
  delay: 4000
}, {
  role: "agent",
  text: "Intéressant. Quel projet vous a le plus marqué ?",
  delay: 12000
}, {
  role: "user",
  text: "Chez mon précédent employeur, j'ai mené la refonte de la plateforme de paiement. On a réduit les erreurs de 40% et augmenté le taux de conversion de 15%.",
  delay: 18000
}, {
  role: "agent",
  text: "Excellent ! Comment avez-vous géré les défis techniques ?",
  delay: 28000
}, {
  role: "user",
  text: "On a d'abord fait un audit complet, puis on a priorisé les quick wins. J'ai mis en place des tests automatisés pour sécuriser les migrations.",
  delay: 34000
}, {
  role: "agent",
  text: "Très bien structuré. Pourquoi ce poste vous intéresse ?",
  delay: 44000
}];
const DEMO_TIPS: DemoTip[] = [{
  type: "success",
  message: "Bonne présentation structurée",
  delay: 8000
}, {
  type: "success",
  message: "📹 Bon contact visuel",
  delay: 16000
}, {
  type: "success",
  message: "Excellent usage de chiffres concrets",
  delay: 24000
}, {
  type: "info",
  message: "📹 Posture un peu fermée, détendez-vous",
  delay: 32000
}, {
  type: "info",
  message: "Pensez à la méthode STAR",
  delay: 42000
}];
const SCORE_EVOLUTION = [{
  delay: 0,
  scores: {
    clarity: 0,
    structure: 0,
    examples: 0,
    confidence: 0,
    posture: 0
  }
}, {
  delay: 8000,
  scores: {
    clarity: 58,
    structure: 52,
    examples: 40,
    confidence: 50,
    posture: 62
  }
}, {
  delay: 16000,
  scores: {
    clarity: 62,
    structure: 55,
    examples: 45,
    confidence: 54,
    posture: 70
  }
}, {
  delay: 24000,
  scores: {
    clarity: 68,
    structure: 62,
    examples: 75,
    confidence: 60,
    posture: 65
  }
}, {
  delay: 32000,
  scores: {
    clarity: 70,
    structure: 65,
    examples: 78,
    confidence: 62,
    posture: 55
  }
}, {
  delay: 40000,
  scores: {
    clarity: 74,
    structure: 72,
    examples: 82,
    confidence: 68,
    posture: 68
  }
}];
const DEMO_DURATION = 50000; // 50 seconds

interface LiveInterviewDemoProps {
  locale: string;
}
export function LiveInterviewDemo(t0) {
  const $ = _c(120);
  const {
    locale
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [messages, setMessages] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      clarity: 0,
      structure: 0,
      examples: 0,
      confidence: 0,
      posture: 0
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [scores, setScores] = useState(t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const [tips, setTips] = useState(t3);
  const [isTyping, setIsTyping] = useState(false);
  const [currentTypingRole, setCurrentTypingRole] = useState(null);
  const [loopKey, setLoopKey] = useState(0);
  const [timer, setTimer] = useState(0);
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const timers = [];
      setMessages([]);
      setScores({
        clarity: 0,
        structure: 0,
        examples: 0,
        confidence: 0,
        posture: 0
      });
      setTips([]);
      setIsTyping(false);
      setCurrentTypingRole(null);
      setTimer(0);
      const timerInterval = setInterval(() => {
        setTimer(_temp);
      }, 1000);
      timers.push(timerInterval as unknown as NodeJS.Timeout);
      DEMO_MESSAGES.forEach(msg => {
        timers.push(setTimeout(() => {
          setIsTyping(true);
          setCurrentTypingRole(msg.role);
        }, msg.delay));
        timers.push(setTimeout(() => {
          setIsTyping(false);
          setCurrentTypingRole(null);
          setMessages(prev => [...prev, msg]);
        }, msg.delay + 1500));
      });
      DEMO_TIPS.forEach(tip => {
        timers.push(setTimeout(() => {
          setTips(prev_0 => [...prev_0, tip]);
        }, tip.delay));
        timers.push(setTimeout(() => {
          setTips(prev_1 => prev_1.filter(t_0 => t_0.message !== tip.message));
        }, tip.delay + 5000));
      });
      SCORE_EVOLUTION.forEach(t5 => {
        const {
          delay,
          scores: newScores
        } = t5;
        timers.push(setTimeout(() => {
          setScores(newScores);
        }, delay));
      });
      const resetTimer = setTimeout(() => {
        clearInterval(timerInterval);
        setLoopKey(_temp2);
      }, DEMO_DURATION);
      timers.push(resetTimer);
      return () => timers.forEach(_temp3);
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== loopKey) {
    t5 = [loopKey];
    $[4] = loopKey;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  useEffect(t4, t5);
  const overallScore = Math.round((scores.clarity + scores.structure + scores.examples + scores.confidence + scores.posture) / 5);
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Badge variant="destructive" className="gap-1 animate-pulse"><span className="w-2 h-2 rounded-full bg-white" />Live</Badge>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const t7 = locale === "fr" ? "Entretien Live" : "Live Interview";
  let t8;
  if ($[7] !== t7) {
    t8 = <div className="flex items-center gap-3">{t6}<span className="font-medium text-sm">{t7}</span></div>;
    $[7] = t7;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const t9 = String(Math.floor(timer / 60));
  let t10;
  if ($[9] !== t9) {
    t10 = t9.padStart(2, "0");
    $[9] = t9;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  const t11 = String(timer % 60);
  let t12;
  if ($[11] !== t11) {
    t12 = t11.padStart(2, "0");
    $[11] = t11;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  let t13;
  if ($[13] !== t10 || $[14] !== t12) {
    t13 = <div className="flex items-center gap-2 text-muted-foreground"><span className="text-sm font-mono">{t10}:{t12}</span></div>;
    $[13] = t10;
    $[14] = t12;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  let t14;
  if ($[16] !== t13 || $[17] !== t8) {
    t14 = <div className="border-b bg-background/95 px-4 py-3 flex items-center justify-between">{t8}{t13}</div>;
    $[16] = t13;
    $[17] = t8;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  let t15;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <span className="text-sm font-medium flex items-center gap-2"><Sparkles className="w-4 h-4 text-primary" />Score</span>;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  const t16 = `text-2xl font-bold ${overallScore >= 60 ? "text-green-500" : overallScore >= 40 ? "text-yellow-500" : overallScore > 0 ? "text-yellow-500" : "text-muted-foreground"}`;
  const t17 = overallScore > 0 ? overallScore : "-";
  let t18;
  if ($[20] !== t16 || $[21] !== t17) {
    t18 = <div className="flex items-center justify-between mb-2">{t15}<span className={t16}>{t17}</span></div>;
    $[20] = t16;
    $[21] = t17;
    $[22] = t18;
  } else {
    t18 = $[22];
  }
  let t19;
  if ($[23] !== overallScore) {
    t19 = <Progress value={overallScore} className="h-2" />;
    $[23] = overallScore;
    $[24] = t19;
  } else {
    t19 = $[24];
  }
  let t20;
  if ($[25] !== t18 || $[26] !== t19) {
    t20 = <Card><CardContent className="pt-4 pb-4">{t18}{t19}</CardContent></Card>;
    $[25] = t18;
    $[26] = t19;
    $[27] = t20;
  } else {
    t20 = $[27];
  }
  const t21 = locale === "fr" ? "Analyse temps r\xE9el" : "Real-time Analysis";
  let t22;
  if ($[28] !== t21) {
    t22 = <h3 className="text-xs font-medium text-muted-foreground mb-3">{t21}</h3>;
    $[28] = t21;
    $[29] = t22;
  } else {
    t22 = $[29];
  }
  const t23 = locale === "fr" ? "Clart\xE9" : "Clarity";
  let t24;
  if ($[30] !== scores.clarity || $[31] !== t23) {
    t24 = {
      key: "clarity",
      label: t23,
      value: scores.clarity,
      icon: MessageSquare
    };
    $[30] = scores.clarity;
    $[31] = t23;
    $[32] = t24;
  } else {
    t24 = $[32];
  }
  let t25;
  if ($[33] !== scores.structure) {
    t25 = {
      key: "structure",
      label: "Structure",
      value: scores.structure,
      icon: Target
    };
    $[33] = scores.structure;
    $[34] = t25;
  } else {
    t25 = $[34];
  }
  const t26 = locale === "fr" ? "Exemples" : "Examples";
  let t27;
  if ($[35] !== scores.examples || $[36] !== t26) {
    t27 = {
      key: "examples",
      label: t26,
      value: scores.examples,
      icon: Lightbulb
    };
    $[35] = scores.examples;
    $[36] = t26;
    $[37] = t27;
  } else {
    t27 = $[37];
  }
  const t28 = locale === "fr" ? "Confiance" : "Confidence";
  let t29;
  if ($[38] !== scores.confidence || $[39] !== t28) {
    t29 = {
      key: "confidence",
      label: t28,
      value: scores.confidence,
      icon: TrendingUp
    };
    $[38] = scores.confidence;
    $[39] = t28;
    $[40] = t29;
  } else {
    t29 = $[40];
  }
  const t30 = locale === "fr" ? "Posture" : "Posture";
  let t31;
  if ($[41] !== scores.posture || $[42] !== t30) {
    t31 = {
      key: "posture",
      label: t30,
      value: scores.posture,
      icon: Video
    };
    $[41] = scores.posture;
    $[42] = t30;
    $[43] = t31;
  } else {
    t31 = $[43];
  }
  let t32;
  if ($[44] !== t24 || $[45] !== t25 || $[46] !== t27 || $[47] !== t29 || $[48] !== t31) {
    t32 = [t24, t25, t27, t29, t31].map(_temp4);
    $[44] = t24;
    $[45] = t25;
    $[46] = t27;
    $[47] = t29;
    $[48] = t31;
    $[49] = t32;
  } else {
    t32 = $[49];
  }
  let t33;
  if ($[50] !== t22 || $[51] !== t32) {
    t33 = <Card><CardContent className="pt-4 pb-4 space-y-3">{t22}{t32}</CardContent></Card>;
    $[50] = t22;
    $[51] = t32;
    $[52] = t33;
  } else {
    t33 = $[52];
  }
  let t34;
  if ($[53] !== tips) {
    t34 = tips.map(_temp5);
    $[53] = tips;
    $[54] = t34;
  } else {
    t34 = $[54];
  }
  let t35;
  if ($[55] !== t34) {
    t35 = <div className="space-y-2 min-h-[80px]">{t34}</div>;
    $[55] = t34;
    $[56] = t35;
  } else {
    t35 = $[56];
  }
  let t36;
  if ($[57] !== t20 || $[58] !== t33 || $[59] !== t35) {
    t36 = <div className="border-r p-4 space-y-4 bg-muted/20 hidden md:block overflow-hidden">{t20}{t33}{t35}</div>;
    $[57] = t20;
    $[58] = t33;
    $[59] = t35;
    $[60] = t36;
  } else {
    t36 = $[60];
  }
  let t37;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <div className="w-20 h-20 md:w-28 md:h-28 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 border-2 border-primary/30 flex items-center justify-center"><User className="w-10 h-10 md:w-14 md:h-14 text-primary/60" /></div>;
    $[61] = t37;
  } else {
    t37 = $[61];
  }
  let t38;
  if ($[62] !== currentTypingRole || $[63] !== isTyping) {
    t38 = isTyping && currentTypingRole === "agent" && <div className="absolute -bottom-1 left-1/2 -translate-x-1/2"><div className="flex gap-1"><span className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce" style={{
          animationDelay: "0ms"
        }} /><span className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce" style={{
          animationDelay: "150ms"
        }} /><span className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce" style={{
          animationDelay: "300ms"
        }} /></div></div>;
    $[62] = currentTypingRole;
    $[63] = isTyping;
    $[64] = t38;
  } else {
    t38 = $[64];
  }
  let t39;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <p className="text-xs text-center mt-1 font-medium">Claire</p>;
    $[65] = t39;
  } else {
    t39 = $[65];
  }
  let t40;
  if ($[66] !== t38) {
    t40 = <div className="relative">{t37}{t38}{t39}</div>;
    $[66] = t38;
    $[67] = t40;
  } else {
    t40 = $[67];
  }
  let t41;
  let t42;
  if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <div className="absolute inset-0 bg-gradient-to-b from-zinc-700/40 via-zinc-800/30 to-slate-700/40" />;
    t42 = <div className="absolute inset-0 backdrop-blur-sm" />;
    $[68] = t41;
    $[69] = t42;
  } else {
    t41 = $[68];
    t42 = $[69];
  }
  let t43;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = <div className="absolute inset-0 flex items-center justify-center"><div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-gradient-to-b from-zinc-300/40 to-zinc-400/30 blur-[2px]" /></div>;
    $[70] = t43;
  } else {
    t43 = $[70];
  }
  let t44;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <div className="w-20 h-20 md:w-28 md:h-28 rounded-xl overflow-hidden border-2 border-muted relative">{t41}{t42}{t43}<div className="absolute bottom-1 left-1/2 -translate-x-1/2"><div className="flex items-center gap-1 bg-background/80 px-1.5 py-0.5 rounded text-[10px]"><Video className="w-2.5 h-2.5" /><span>Vous</span></div></div></div>;
    $[71] = t44;
  } else {
    t44 = $[71];
  }
  let t45;
  if ($[72] !== currentTypingRole || $[73] !== isTyping) {
    t45 = isTyping && currentTypingRole === "user" && <div className="absolute -bottom-1 left-1/2 -translate-x-1/2"><div className="flex gap-1"><span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-bounce" style={{
          animationDelay: "0ms"
        }} /><span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-bounce" style={{
          animationDelay: "150ms"
        }} /><span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-bounce" style={{
          animationDelay: "300ms"
        }} /></div></div>;
    $[72] = currentTypingRole;
    $[73] = isTyping;
    $[74] = t45;
  } else {
    t45 = $[74];
  }
  const t46 = locale === "fr" ? "Vous" : "You";
  let t47;
  if ($[75] !== t46) {
    t47 = <p className="text-xs text-center mt-1 text-muted-foreground">{t46}</p>;
    $[75] = t46;
    $[76] = t47;
  } else {
    t47 = $[76];
  }
  let t48;
  if ($[77] !== t45 || $[78] !== t47) {
    t48 = <div className="relative">{t44}{t45}{t47}</div>;
    $[77] = t45;
    $[78] = t47;
    $[79] = t48;
  } else {
    t48 = $[79];
  }
  let t49;
  if ($[80] !== t40 || $[81] !== t48) {
    t49 = <div className="flex items-center justify-center gap-6 mb-4">{t40}{t48}</div>;
    $[80] = t40;
    $[81] = t48;
    $[82] = t49;
  } else {
    t49 = $[82];
  }
  const t50 = locale === "fr" ? "Entretien pour" : "Interview for";
  let t51;
  if ($[83] !== t50) {
    t51 = <p className="text-sm text-muted-foreground mb-2">{t50}</p>;
    $[83] = t50;
    $[84] = t51;
  } else {
    t51 = $[84];
  }
  let t52;
  if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
    t52 = <p className="text-base font-semibold">Software Engineer @ TechCorp</p>;
    $[85] = t52;
  } else {
    t52 = $[85];
  }
  let t53;
  if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center"><Mic className="w-4 h-4" /></div>;
    $[86] = t53;
  } else {
    t53 = $[86];
  }
  let t54;
  if ($[87] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center border border-primary/30"><Video className="w-4 h-4 text-primary" /></div>;
    $[87] = t54;
  } else {
    t54 = $[87];
  }
  let t55;
  if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
    t55 = <div className="flex items-center gap-3 mt-6">{t53}{t54}<div className="w-12 h-12 rounded-full bg-destructive flex items-center justify-center"><Phone className="w-5 h-5 text-white" /></div></div>;
    $[88] = t55;
  } else {
    t55 = $[88];
  }
  let t56;
  if ($[89] !== t49 || $[90] !== t51) {
    t56 = <div className="flex flex-col items-center justify-center p-6 bg-gradient-to-b from-background to-muted/20">{t49}{t51}{t52}{t55}</div>;
    $[89] = t49;
    $[90] = t51;
    $[91] = t56;
  } else {
    t56 = $[91];
  }
  let t57;
  if ($[92] === Symbol.for("react.memo_cache_sentinel")) {
    t57 = <MessageSquare className="w-3 h-3" />;
    $[92] = t57;
  } else {
    t57 = $[92];
  }
  const t58 = locale === "fr" ? "Transcription" : "Transcript";
  let t59;
  if ($[93] !== t58) {
    t59 = <h3 className="text-xs font-medium text-muted-foreground mb-3 flex items-center gap-2">{t57}{t58}</h3>;
    $[93] = t58;
    $[94] = t59;
  } else {
    t59 = $[94];
  }
  let t60;
  if ($[95] !== locale || $[96] !== messages) {
    let t61;
    if ($[98] !== locale) {
      t61 = (msg_0, i_0) => <div key={i_0} className={`text-xs p-2 rounded-lg animate-in fade-in slide-in-from-bottom-2 duration-300 ${msg_0.role === "agent" ? "bg-muted" : "bg-primary/10 ml-2"}`}><span className={`font-medium ${msg_0.role === "agent" ? "text-primary" : "text-foreground"}`}>{msg_0.role === "agent" ? "Claire" : locale === "fr" ? "Vous" : "You"}:</span>{" "}<span className="text-muted-foreground">{msg_0.text}</span></div>;
      $[98] = locale;
      $[99] = t61;
    } else {
      t61 = $[99];
    }
    t60 = messages.map(t61);
    $[95] = locale;
    $[96] = messages;
    $[97] = t60;
  } else {
    t60 = $[97];
  }
  let t61;
  if ($[100] !== currentTypingRole || $[101] !== isTyping || $[102] !== locale) {
    t61 = isTyping && currentTypingRole === "user" && <div className="text-xs p-2 rounded-lg bg-primary/10 ml-2 animate-pulse"><span className="font-medium">{locale === "fr" ? "Vous" : "You"}:</span>{" "}<span className="text-muted-foreground">...</span></div>;
    $[100] = currentTypingRole;
    $[101] = isTyping;
    $[102] = locale;
    $[103] = t61;
  } else {
    t61 = $[103];
  }
  let t62;
  if ($[104] !== t60 || $[105] !== t61) {
    t62 = <div className="space-y-3 max-h-[320px] overflow-y-auto">{t60}{t61}</div>;
    $[104] = t60;
    $[105] = t61;
    $[106] = t62;
  } else {
    t62 = $[106];
  }
  let t63;
  if ($[107] !== t59 || $[108] !== t62) {
    t63 = <div className="border-l p-4 bg-muted/20 hidden md:block">{t59}{t62}</div>;
    $[107] = t59;
    $[108] = t62;
    $[109] = t63;
  } else {
    t63 = $[109];
  }
  let t64;
  if ($[110] !== t36 || $[111] !== t56 || $[112] !== t63) {
    t64 = <div className="grid md:grid-cols-[280px_1fr_280px] min-h-[400px] w-full">{t36}{t56}{t63}</div>;
    $[110] = t36;
    $[111] = t56;
    $[112] = t63;
    $[113] = t64;
  } else {
    t64 = $[113];
  }
  let t65;
  if ($[114] !== t14 || $[115] !== t64) {
    t65 = <div className="rounded-2xl border bg-card overflow-hidden shadow-xl max-w-full">{t14}{t64}</div>;
    $[114] = t14;
    $[115] = t64;
    $[116] = t65;
  } else {
    t65 = $[116];
  }
  let t66;
  if ($[117] === Symbol.for("react.memo_cache_sentinel")) {
    t66 = <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 gap-1.5 px-4 py-1.5 shadow-lg"><Sparkles className="w-3.5 h-3.5" />Premium</Badge>;
    $[117] = t66;
  } else {
    t66 = $[117];
  }
  let t67;
  if ($[118] !== t65) {
    t67 = <div className="relative w-full max-w-5xl mx-auto">{t65}{t66}</div>;
    $[118] = t65;
    $[119] = t67;
  } else {
    t67 = $[119];
  }
  return t67;
}
function _temp5(tip_0, i) {
  return <Card key={i} className={`border-l-4 animate-in slide-in-from-left duration-300 ${tip_0.type === "success" ? "border-l-green-500" : "border-l-primary"}`}><CardContent className="p-2"><div className="flex items-start gap-1.5"><CheckCircle2 className="w-3 h-3 text-green-500 mt-0.5 shrink-0" /><p className="text-xs break-words">{tip_0.message}</p></div></CardContent></Card>;
}
function _temp4(t0) {
  const {
    key,
    label,
    value,
    icon: Icon
  } = t0;
  return <div key={key} className="space-y-1"><div className="flex items-center justify-between text-xs"><span className="flex items-center gap-1.5 text-muted-foreground"><Icon className="w-3 h-3" />{label}</span><span className={`font-medium ${value >= 60 ? "text-green-500" : value >= 40 ? "text-yellow-500" : value > 0 ? "text-yellow-500" : "text-muted-foreground"}`}>{value > 0 ? value : "-"}</span></div><Progress value={value} className="h-1 transition-all duration-700" /></div>;
}
function _temp3(t_1) {
  return clearTimeout(t_1);
}
function _temp2(k) {
  return k + 1;
}
function _temp(t) {
  return t + 1;
}